import { toast } from "react-toastify";
import {
  useSendDocumentEmail,
  useGetDocumentWhatsAppRecipients,
} from "http-hooks/document/useDocument";

/**
 * Custom hook for document actions (Email, WhatsApp, Download, Print)
 * Generic hook that works for any document type by sending form data to backend API
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.documentType - Type of document (e.g., "Sale Bill", "Voucher", "Shipping Info")
 * @param {string} params.documentNumber - Document number
 * @param {string} params.pdfUrl - URL to the PDF document
 */
const useDocumentActions = ({
  documentType = "",
  documentNumber = "",
  pdfUrl = "",
}) => {
  // React Query mutations
  const { mutateAsync: sendEmail, isPending: isSendingEmail } =
    useSendDocumentEmail();
  const { mutateAsync: getWhatsAppRecipients, isPending: isGettingWhatsApp } =
    useGetDocumentWhatsAppRecipients();

  /**
   * Handle sending email - Sends data to backend API
   * @param {Object} emailData - Email data containing documentId, documentType, recipients, etc.
   * @param {string} emailData.documentId - Document ID
   * @param {string} emailData.documentType - Document type
   * @param {string} emailData.documentNumber - Document number
   * @param {string} emailData.pdfUrl - PDF URL
   * @param {string} emailData.htmlUrl - HTML URL (optional)
   * @param {Object} emailData.recipients - Selected recipients
   */
  const handleSendEmail = async (emailData) => {
    try {
      console.log("=== SENDING EMAIL ===");
      console.log(
        "Document:",
        emailData.documentType,
        emailData.documentNumber
      );
      console.log("Recipients:", emailData.recipients);

      const result = await sendEmail(emailData);

      console.log("Email sent to:", result.recipients);
    } catch (error) {
      console.error("Error sending email:", error);
      // Error toast is handled in service layer
    }
  };

  /**
   * Handle WhatsApp sending - Gets WhatsApp numbers from backend and opens WhatsApp Web
   * @param {Object} whatsappData - WhatsApp data containing documentId, documentType, recipients, etc.
   * @param {string} whatsappData.documentId - Document ID
   * @param {string} whatsappData.documentType - Document type
   * @param {string} whatsappData.documentNumber - Document number
   * @param {string} whatsappData.pdfUrl - PDF URL
   * @param {Object} whatsappData.recipients - Selected recipients
   */
  const handleSendWhatsApp = async (whatsappData) => {
    try {
      console.log("=== SENDING WHATSAPP ===");
      console.log(
        "Document:",
        whatsappData.documentType,
        whatsappData.documentNumber
      );
      console.log("Recipients:", whatsappData.recipients);

      const result = await getWhatsAppRecipients(whatsappData);
      const { recipients, pdfUrl, documentType, documentNumber } = result;

      if (recipients.length === 0) {
        toast.warning("No WhatsApp numbers found for selected recipients");
        return;
      }

      // Only open WhatsApp for the FIRST recipient (one at a time)
      const recipient = recipients[0];
      const message = encodeURIComponent(
        `*${documentType} #${documentNumber} - System generated PDF Link*\n\n${pdfUrl}\n\nMessage Sent from Agentio.in Platform`
      );

      const formattedNumber = recipient.number.replace(/\D/g, "");
      const whatsappNumber = formattedNumber.startsWith("91")
        ? formattedNumber
        : `91${formattedNumber}`;
      const whatsappLink = `https://wa.me/${whatsappNumber}?text=${message}`;

      window.open(whatsappLink, "_blank");

      if (recipients.length > 1) {
        toast.info(
          `WhatsApp opened for ${recipient.name || recipient.type}. ${
            recipients.length - 1
          } more contact(s) available.`
        );
      } else {
        toast.success(`WhatsApp opened for ${recipient.name || recipient.type}`);
      }
    } catch (error) {
      console.error("Error sending WhatsApp:", error);
      // Error toast is handled in service layer
    }
  };

  /**
   * Handle PDF download - Fetches the file and triggers browser download
   */
  const handleDownloadPdf = async () => {
    if (!pdfUrl) {
      console.error("No PDF URL provided");
      alert("PDF URL not available");
      return;
    }

    try {
      console.log("Downloading PDF:", pdfUrl);

      // Fetch the PDF file
      const response = await fetch(pdfUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }

      // Convert to blob
      const blob = await response.blob();

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${documentType}_${documentNumber}.pdf`.replace(
        /\s+/g,
        "_"
      );
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      console.log("PDF download triggered successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  /**
   * Handle print
   */
  const handlePrint = () => {
    if (!pdfUrl) {
      console.error("No PDF URL provided");
      return;
    }

    console.log("Opening print dialog for:", pdfUrl);

    // Open PDF in new window and trigger print
    const printWindow = window.open(pdfUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const isLoading = isSendingEmail || isGettingWhatsApp;

  return {
    handleSendEmail,
    handleSendWhatsApp,
    handleDownloadPdf,
    handlePrint,
    isLoading,
  };
};

export default useDocumentActions;
